import { AccountType, RRSPAccount_PlanType } from "../../generated/core/v1/accounts";
import type { Date as PDate } from "../../generated/core/v1/date";
import { HouseholdExpenseDetails_Type } from "../../generated/core/v1/expense";
import {
  type Family,
  type FinancialPriority,
  PriorityKind,
  RetirementMethod,
} from "../../generated/core/v1/family";
import { IncomeKind, PensionType } from "../../generated/core/v1/person";
import type { DateSchedule } from "../../generated/optimizer/v1/date_schedule";
import {
  type AccountsSection_Account,
  type HouseholdSection,
  HouseholdSection_Member_Role as Role,
  type IncomeSection_Income,
  PrioritiesSection_Frequency as Frequency,
  type PrioritiesSection,
  type PrioritiesSection_Priority,
  type SafetyNetSection_PensionAnswer,
  type SpendingSection,
} from "../../generated/core/v1/questionnaire";
import { isSet } from "../lib/dates";
import { PRIORITY_KIND_LABEL } from "../plan-view/entities";
import type { DraftPerson, DraftPriority } from "./draft";

/**
 * Family → questionnaire sections, for partial saves (Update Data, Priorities → Edit). A section
 * that is set replaces that part of the family, so each builder lists every existing entity of its
 * kinds (by uuid) with the edits applied. Values pass through; nothing is computed except turning a
 * stored per-period amount into the per-month or per-year figure the section asks for.
 */

const copyDate = (d: PDate | undefined): PDate | undefined => (isSet(d) ? { ...d } : undefined);

/** Occurrences a year of a stored schedule (monthly when unknown). */
export function perYear(s: DateSchedule | undefined): number {
  const k = s?.schedule;
  switch (k?.oneofKind) {
    case "monthlyRecurring":
      return 12 / Math.max(1, k.monthlyRecurring.period || 1);
    case "yearlyRecurring":
      return 1 / Math.max(1, k.yearlyRecurring.period || 1);
    case "nDayPeriodic":
      return 365 / Math.max(1, k.nDayPeriodic.period || 1);
    default:
      return 12;
  }
}

// ------------------------------------------------------------------ priorities

function frequencyOf(p: FinancialPriority): Frequency {
  const k = p.dateSchedule?.schedule;
  if (k?.oneofKind === "monthlyRecurring") return Frequency.MONTHLY;
  if (k?.oneofKind === "yearlyRecurring") {
    switch (k.yearlyRecurring.period) {
      case 2:
        return Frequency.EVERY_2_YEARS;
      case 3:
        return Frequency.EVERY_3_YEARS;
      case 5:
        return Frequency.EVERY_5_YEARS;
      default:
        return Frequency.YEARLY;
    }
  }
  return Frequency.YEARLY;
}

/** The family's priorities as the editor shows them: its goals, then its home-buying goals (kind
 * BUY_HOME, with the price and the down payment as stored; unset = the engine's 20%). */
export function prioritiesFromFamily(family: Family): DraftPriority[] {
  return [
    ...family.financialPriorities.map((p) => ({
      uuid: p.uuid,
      kind: p.kind,
      // a goal stored without a name (the editor has no name box) is named by its kind, as shown
      name: p.name.trim() || PRIORITY_KIND_LABEL[p.kind],
      cost: p.requiredFunding,
      when: copyDate(p.nextDueDate),
      importance: p.level || undefined,
      repeats: !!p.isRecurring,
      frequency: frequencyOf(p),
      until: copyDate(p.endDate),
      beneficiaryUuids: [...p.beneficiaryUuids],
    })),
    ...family.homebuyingPriorities.map((p) => ({
      uuid: p.uuid,
      kind: PriorityKind.BUY_HOME,
      name: p.name.trim() || PRIORITY_KIND_LABEL[PriorityKind.BUY_HOME],
      cost: p.downPayment,
      housePrice: p.housePrice || undefined,
      when: copyDate(p.dueDate),
      importance: p.level || undefined,
      repeats: false,
      frequency: Frequency.YEARLY,
      beneficiaryUuids: [],
    })),
  ];
}

/** One priority's answer. A home-buying goal sends its price (`cost` is its down payment) and never
 * repeats; the engine saves it as a home-buying goal. */
export function priorityAnswer(
  p: DraftPriority,
  childUuids?: Set<string>,
): PrioritiesSection_Priority {
  const home = p.kind === PriorityKind.BUY_HOME;
  const repeats = !home && p.repeats;
  return {
    uuid: p.uuid,
    kind: p.kind,
    name: p.name.trim(),
    cost: p.cost,
    housePrice: home ? p.housePrice : undefined,
    when: copyDate(p.when),
    importance: p.importance ?? 0,
    repeats,
    frequency: repeats ? p.frequency : Frequency.UNSPECIFIED,
    until: repeats ? copyDate(p.until) : undefined,
    beneficiaryUuids: home
      ? []
      : childUuids
        ? p.beneficiaryUuids.filter((u) => childUuids.has(u))
        : [...p.beneficiaryUuids],
  };
}

export function prioritiesSection(
  list: DraftPriority[],
  retirementImportance: number | undefined,
  family?: Family,
): PrioritiesSection {
  const kids = family ? new Set(family.dependents.map((d) => d.uuid)) : undefined;
  return {
    priorities: list.map((p) => priorityAnswer(p, kids)),
    retirementImportance: retirementImportance ?? 0,
  };
}

export function kidsFromFamily(family: Family): DraftPerson[] {
  return family.dependents.map((d) => ({
    uuid: d.uuid,
    givenName: d.givenName,
    familyName: d.familyName,
    dob: copyDate(d.dob),
  }));
}

// ------------------------------------------------------------------ household

export function householdSection(family: Family): HouseholdSection {
  return {
    members: family.persons.map((p, i) => ({
      uuid: p.uuid,
      role: i === 0 ? Role.SELF : Role.PARTNER,
      givenName: p.givenName,
      familyName: p.familyName,
      dob: copyDate(p.dob),
    })),
    maritalStatus: family.maritalStatus,
    children: family.dependents.map((d) => ({
      uuid: d.uuid,
      givenName: d.givenName,
      familyName: d.familyName,
      dob: copyDate(d.dob),
    })),
    province: family.province,
  };
}

// ------------------------------------------------------------------ income

export function incomesFromFamily(family: Family): IncomeSection_Income[] {
  return family.persons.flatMap((p) => [
    ...p.jobs.map((j) => ({
      uuid: j.uuid,
      memberUuid: p.uuid,
      kind: j.kind || IncomeKind.SALARY,
      name: j.title,
      annualAmountBeforeTax: j.preTaxAnnualIncome,
      occupation: j.occupation,
      startDate: copyDate(j.startDate),
      endDate: copyDate(j.endDate),
      groupRrspPlan: j.groupRrspPlan ? { ...j.groupRrspPlan } : undefined,
    })),
    ...p.untaxedIncomes.map((u) => ({
      uuid: u.uuid,
      memberUuid: p.uuid,
      kind: u.kind || IncomeKind.OTHER,
      name: u.name,
      annualAmountBeforeTax: u.amountPerPeriod * perYear(u.dateSchedule),
      occupation: "",
      startDate: copyDate(u.startDate),
      endDate: copyDate(u.endDate),
    })),
  ]);
}

// ------------------------------------------------------------------ accounts

export function accountsFromFamily(family: Family): AccountsSection_Account[] {
  return family.persons.flatMap((p) => {
    const base = (uuid: string, name: string, balance: number | undefined) => ({
      uuid,
      name,
      memberUuid: p.uuid,
      balance: balance ?? 0,
      beneficiaryUuids: [] as string[],
      savings: false,
      interestRate: undefined as number | undefined,
      planType: RRSPAccount_PlanType.UNSPECIFIED,
    });
    return [
      ...p.chequingAccounts.map((a) => ({
        ...base(a.uuid, a.name, a.currentValue),
        kind: AccountType.CHEQUING_ACCOUNT,
        savings: a.type === 2,
        interestRate: a.interestApr,
      })),
      ...p.tfsaAccounts.map((a) => ({
        ...base(a.uuid, a.name, a.currentValue),
        kind: AccountType.TFSA_ACCOUNT,
      })),
      ...p.rrspAccounts.map((a) => ({
        ...base(a.uuid, a.name, a.currentValue),
        kind: AccountType.RRSP_ACCOUNT,
        planType: a.planType,
      })),
      ...p.fhsaAccounts.map((a) => ({
        ...base(a.uuid, a.name, a.currentValue),
        kind: AccountType.FHSA_ACCOUNT,
      })),
      ...p.respAccounts.map((a) => ({
        ...base(a.uuid, a.name, a.currentValue),
        kind: AccountType.RESP_ACCOUNT,
        beneficiaryUuids: [...a.beneficiaryUuids],
      })),
      ...p.tradingAccounts.map((a) => ({
        ...base(a.uuid, a.name, a.currentValue),
        kind: AccountType.NON_REGISTERED_INVESTMENT_ACCOUNT,
      })),
      ...p.rrifAccounts.map((a) => ({
        ...base(a.uuid, a.name, a.currentValue),
        kind: AccountType.RRIF_ACCOUNT,
      })),
      ...p.dcPensionAccounts.map((a) => ({
        ...base(a.uuid, a.name, a.currentValue),
        kind: AccountType.DC_PENSION_ACCOUNT,
      })),
    ];
  });
}

export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  [AccountType.UNSPECIFIED]: "Account",
  [AccountType.CHEQUING_ACCOUNT]: "Chequing",
  [AccountType.RRSP_ACCOUNT]: "RRSP",
  [AccountType.TFSA_ACCOUNT]: "TFSA",
  [AccountType.NON_REGISTERED_INVESTMENT_ACCOUNT]: "Non-registered",
  [AccountType.RESP_ACCOUNT]: "RESP",
  [AccountType.FHSA_ACCOUNT]: "FHSA",
  [AccountType.DC_PENSION_ACCOUNT]: "Workplace pension account",
  [AccountType.RRIF_ACCOUNT]: "RRIF",
};

// ------------------------------------------------------------------ spending

export function spendingFromFamily(family: Family): SpendingSection {
  const monthly = (t: HouseholdExpenseDetails_Type) =>
    family.expenses
      .filter(
        (e) =>
          e.details.oneofKind === "householdExpenseDetails" &&
          e.details.householdExpenseDetails.type === t,
      )
      .reduce((s, e) => s + (e.amountDuePerPeriod * perYear(e.dateSchedule)) / 12, 0);
  const rent = monthly(HouseholdExpenseDetails_Type.RENT);
  return {
    essentialMonthly: Math.round(monthly(HouseholdExpenseDetails_Type.ESSENTIAL_EXPENSES)),
    lifestyleMonthly: Math.round(monthly(HouseholdExpenseDetails_Type.NON_ESSENTIAL_EXPENSES)),
    rentMonthly: rent > 0 ? Math.round(rent) : undefined,
  };
}

// ------------------------------------------------------------------ pensions and retirement

export function pensionsFromFamily(family: Family): SafetyNetSection_PensionAnswer[] {
  return family.persons.flatMap((p) =>
    p.pensions.map((pn) => ({
      uuid: pn.uuid,
      memberUuid: p.uuid,
      type: pn.pensionType || PensionType.DEFINED_BENEFIT,
      jobUuid: p.jobs.find((j) => j.associatedPensionUuid === pn.uuid)?.uuid ?? "",
    })),
  );
}

export type RetirementModel = { method: RetirementMethod; pct?: number; age?: number };

export function retirementFromFamily(family: Family): RetirementModel {
  const goal = family.persons[0]?.retirementGoal;
  return {
    method:
      family.retirementMethod === RetirementMethod.TARGET
        ? RetirementMethod.TARGET
        : RetirementMethod.SPENDING,
    pct: goal?.incomeReplacementPercent,
    age: goal?.retirementAge,
  };
}
